import React, { useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
// The side-effect-free entry: src/index.tsx loads Stripe.js (not on the fill page).
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  CreditCard,
  FileText,
  Loader2,
  Receipt,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CircleDollarSign,
} from "lucide-react";
import {
  useAttachPaymentMethodMutation,
  useBillingAccountQuery,
  useBillingInvoicesQuery,
  useBillingUsageDaysQuery,
  useBillingUsageEventsQuery,
  useBillingUsageMonthsQuery,
  useRemovePaymentMethodMutation,
  useRetryInvoiceMutation,
} from "@/hooks/queries";
import {
  billingService,
  type BillingAccount,
  type UsageBucket,
  type UsageEvent,
} from "@/services/billing";
import {
  formatCents,
  formatDayKey,
  formatMonthKey,
  formatUtcDate,
  formatUtcTime,
  invoiceStatusLabel,
  kindLabel,
} from "@/utils/billingFormat";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const stripePromiseCache = new Map<string, Promise<Stripe | null>>();

function stripePromiseFor(publishableKey: string): Promise<Stripe | null> {
  let promise = stripePromiseCache.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey);
    stripePromiseCache.set(publishableKey, promise);
  }
  return promise;
}

const Billing: React.FC = () => {
  const accountQuery = useBillingAccountQuery();
  const monthsQuery = useBillingUsageMonthsQuery(12);
  const invoicesQuery = useBillingInvoicesQuery(1);

  const account = accountQuery.data;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
      </div>

      {accountQuery.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not load billing</AlertTitle>
          <AlertDescription>Refresh the page or try again later.</AlertDescription>
        </Alert>
      )}

      {!accountQuery.isLoading && account && !account.billing_configured && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Billing not configured</AlertTitle>
          <AlertDescription>
            Stripe keys are not set in this environment. Usage is still tracked; card setup and
            charges are unavailable until an operator configures Stripe.
          </AlertDescription>
        </Alert>
      )}

      {!accountQuery.isLoading && account && !account.has_payment_method && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No payment method on file</AlertTitle>
          <AlertDescription>Go to the Payment method tab to add one.</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="usage">
        <TabsList>
          <TabsTrigger value="usage">
            <FileText className="mr-2 h-4 w-4" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt className="mr-2 h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="payment">
            <CreditCard className="mr-2 h-4 w-4" />
            Payment method
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <CircleDollarSign className="mr-2 h-4 w-4" />
            Pricing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usage" className="mt-4">
          <UsageTab monthsQuery={monthsQuery} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <InvoicesTab invoicesQuery={invoicesQuery} />
        </TabsContent>
        <TabsContent value="payment" className="mt-4">
          {account && <PaymentTab account={account} />}
        </TabsContent>
        <TabsContent value="pricing" className="mt-4">
          <PricingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

function PricingTab() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pricing</CardTitle>
        <CardDescription>Simple usage-based rates for underwriting work.</CardDescription>
      </CardHeader>
      <CardContent className="max-w-2xl space-y-8 text-sm leading-relaxed text-muted-foreground">
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">
            $0.30 per bank statement processed
          </h3>
          <p>
            This covers extraction, reading, and underwriting of each bank statement, as well as any
            subsequent statements pulled via Plaid for final underwriting.
          </p>
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">
            $4.00 flat per background search
          </h3>
          <p>One flat fee for a full background search on the business and owner, including:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Secretary of State (SOS) business registrations</li>
            <li>Business name and address verification</li>
            <li>TIN / EIN matching</li>
            <li>Owner identity verification</li>
            <li>OFAC and PEP screening</li>
            <li>Liens</li>
            <li>Litigation history</li>
            <li>Bankruptcy records</li>
            <li>Related same-state business registrations linked to the owner</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function UsageTab({ monthsQuery }: { monthsQuery: ReturnType<typeof useBillingUsageMonthsQuery> }) {
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const daysQuery = useBillingUsageDaysQuery(expandedMonth);
  const eventsQuery = useBillingUsageEventsQuery(expandedDay);

  if (monthsQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (monthsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Could not load usage.</AlertDescription>
      </Alert>
    );
  }

  const months = monthsQuery.data?.months ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Monthly usage</CardTitle>
        <CardDescription>
          Expand a month for per-day totals, then a day for individual events. Days use UTC.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {months.length === 0 ? (
          <p className="text-sm text-muted-foreground">No usage recorded yet.</p>
        ) : (
          months.map((month) => (
            <MonthRow
              key={month.key}
              bucket={month}
              expanded={expandedMonth === month.key}
              onToggle={() => {
                setExpandedMonth((cur) => (cur === month.key ? null : month.key));
                setExpandedDay(null);
              }}
              days={expandedMonth === month.key ? (daysQuery.data?.days ?? []) : []}
              daysLoading={expandedMonth === month.key && daysQuery.isLoading}
              daysError={expandedMonth === month.key && daysQuery.isError}
              expandedDay={expandedDay}
              onToggleDay={(key) => setExpandedDay((cur) => (cur === key ? null : key))}
              events={expandedDay ? (eventsQuery.data?.results ?? []) : []}
              eventsTotal={expandedDay ? (eventsQuery.data?.count ?? 0) : 0}
              eventsLoading={Boolean(expandedDay) && eventsQuery.isLoading}
              eventsError={Boolean(expandedDay) && eventsQuery.isError}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function MonthRow({
  bucket,
  expanded,
  onToggle,
  days,
  daysLoading,
  daysError,
  expandedDay,
  onToggleDay,
  events,
  eventsTotal,
  eventsLoading,
  eventsError,
}: {
  bucket: UsageBucket;
  expanded: boolean;
  onToggle: () => void;
  days: UsageBucket[];
  daysLoading: boolean;
  daysError: boolean;
  expandedDay: string | null;
  onToggleDay: (key: string) => void;
  events: UsageEvent[];
  eventsTotal: number;
  eventsLoading: boolean;
  eventsError: boolean;
}) {
  const stmts = bucket.usage.statement_processed?.count ?? 0;
  const searches = bucket.usage.baselayer_run?.count ?? 0;
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <div className="flex-1">
          <div className="font-medium">{formatMonthKey(bucket.key)}</div>
          <div className="text-xs text-muted-foreground">
            {bucket.applications_created} applications · {stmts} statements · {searches} searches
          </div>
        </div>
        <div className="text-sm font-medium">{formatCents(bucket.total_cents)}</div>
      </button>
      {expanded && (
        <div className="border-t bg-muted/20 px-2 py-2">
          {daysLoading ? (
            <div className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading days…
            </div>
          ) : daysError ? (
            <Alert variant="destructive" className="m-2">
              <AlertDescription>Could not load days for this month.</AlertDescription>
            </Alert>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Day (UTC)</TableHead>
                  <TableHead className="text-right">Apps</TableHead>
                  <TableHead className="text-right">Statements</TableHead>
                  <TableHead className="text-right">Searches</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {days
                  .filter((d) => d.total_cents > 0 || d.applications_created > 0)
                  .map((day) => (
                    <React.Fragment key={day.key}>
                      <TableRow
                        className={cn("cursor-pointer", expandedDay === day.key && "bg-muted/40")}
                        onClick={() => onToggleDay(day.key)}
                      >
                        <TableCell>{formatDayKey(day.key)}</TableCell>
                        <TableCell className="text-right">{day.applications_created}</TableCell>
                        <TableCell className="text-right">
                          {day.usage.statement_processed?.count ?? 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {day.usage.baselayer_run?.count ?? 0}
                        </TableCell>
                        <TableCell className="text-right">{formatCents(day.total_cents)}</TableCell>
                      </TableRow>
                      {expandedDay === day.key && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-background p-3">
                            {eventsLoading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
                              </div>
                            ) : eventsError ? (
                              <Alert variant="destructive">
                                <AlertDescription>
                                  Could not load events for this day.
                                </AlertDescription>
                              </Alert>
                            ) : events.length === 0 ? (
                              <p className="text-sm text-muted-foreground">No events this day.</p>
                            ) : (
                              <ul className="space-y-1 text-sm">
                                {events.map((ev) => (
                                  <li
                                    key={ev.id}
                                    className="flex justify-between gap-4 border-b border-dashed py-1 last:border-0"
                                  >
                                    <span>
                                      {kindLabel(ev.kind)}
                                      {ev.borrower_name ? ` · ${ev.borrower_name}` : ""}
                                      {!ev.billable ? " (not billed)" : ""}
                                    </span>
                                    <span className="shrink-0 text-muted-foreground">
                                      {formatUtcTime(ev.occurred_at)} UTC ·{" "}
                                      {formatCents(ev.amount_cents)}
                                    </span>
                                  </li>
                                ))}
                                {eventsTotal > events.length && (
                                  <li className="pt-1 text-xs text-muted-foreground">
                                    Showing {events.length} of {eventsTotal} events.
                                  </li>
                                )}
                              </ul>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

function InvoicesTab({
  invoicesQuery,
}: {
  invoicesQuery: ReturnType<typeof useBillingInvoicesQuery>;
}) {
  const retry = useRetryInvoiceMutation();
  const [retryError, setRetryError] = useState<string | null>(null);

  if (invoicesQuery.isLoading) return <Skeleton className="h-48 w-full" />;
  if (invoicesQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Could not load invoices.</AlertDescription>
      </Alert>
    );
  }

  const invoices = invoicesQuery.data?.results ?? [];
  const totalCount = invoicesQuery.data?.count ?? invoices.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoices</CardTitle>
        <CardDescription>Closed billing periods and Stripe payment status (UTC).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {retryError && (
          <Alert variant="destructive">
            <AlertDescription>{retryError}</AlertDescription>
          </Alert>
        )}
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      {formatUtcDate(inv.period_start)} – {formatUtcDate(inv.period_end)}
                    </TableCell>
                    <TableCell className="capitalize">{inv.trigger}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "text-sm",
                          inv.status === "paid" && "text-emerald-700",
                          inv.status === "payment_failed" && "text-destructive"
                        )}
                      >
                        {invoiceStatusLabel(inv.status)}
                      </span>
                      {inv.last_error && inv.status !== "paid" && (
                        <p className="max-w-xs truncate text-xs text-muted-foreground">
                          {inv.last_error}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatCents(inv.subtotal_cents)}</TableCell>
                    <TableCell className="space-x-2 text-right">
                      {inv.hosted_invoice_url && (
                        <Button variant="link" size="sm" asChild>
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                            Stripe
                          </a>
                        </Button>
                      )}
                      {inv.invoice_pdf_url && (
                        <Button variant="link" size="sm" asChild>
                          <a href={inv.invoice_pdf_url} target="_blank" rel="noreferrer">
                            PDF
                          </a>
                        </Button>
                      )}
                      {(inv.status === "open" || inv.status === "payment_failed") && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={retry.isPending}
                          onClick={() => {
                            setRetryError(null);
                            retry.mutate(inv.id, {
                              onError: (err: unknown) => {
                                const detail = (
                                  err as { response?: { data?: { detail?: string } } }
                                )?.response?.data?.detail;
                                setRetryError(
                                  detail || "Retry failed. Try again or update the card."
                                );
                              },
                            });
                          }}
                        >
                          Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalCount > invoices.length && (
              <p className="text-xs text-muted-foreground">
                Showing {invoices.length} of {totalCount} invoices.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentTab({ account }: { account: BillingAccount }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const remove = useRemovePaymentMethodMutation();
  const canManageCard = account.billing_configured && Boolean(account.stripe_publishable_key);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Payment method</CardTitle>
        <CardDescription>
          Cards are stored with Stripe and charged off-session when a period closes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {removeError && (
          <Alert variant="destructive">
            <AlertDescription>{removeError}</AlertDescription>
          </Alert>
        )}
        {account.has_payment_method ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3">
            <div>
              <p className="font-medium capitalize">
                {account.card_brand || "Card"} ending in {account.card_last4}
              </p>
              {account.card_exp_month && account.card_exp_year && (
                <p className="text-sm text-muted-foreground">
                  Expires {account.card_exp_month}/{account.card_exp_year}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={!canManageCard}
                onClick={() => setDialogOpen(true)}
              >
                Replace card
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" disabled={remove.isPending}>
                    Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove card?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Invoices that close while no card is on file stay open until a new card is
                      added.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep card</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        setRemoveError(null);
                        remove.mutate(undefined, {
                          onError: () => setRemoveError("Could not remove the card. Try again."),
                        });
                      }}
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">No payment method on file.</p>
            <Button disabled={!canManageCard} onClick={() => setDialogOpen(true)}>
              Add payment method
            </Button>
          </div>
        )}
      </CardContent>

      <AddCardDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        publishableKey={account.stripe_publishable_key}
        billingConfigured={account.billing_configured}
      />
    </Card>
  );
}

function AddCardDialog({
  open,
  onOpenChange,
  publishableKey,
  billingConfigured,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publishableKey: string;
  billingConfigured: boolean;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !billingConfigured) {
      setClientSecret(null);
      setError(null);
      return;
    }
    if (!publishableKey) {
      setClientSecret(null);
      setError("Stripe publishable key is missing in this environment.");
      setLoadingSecret(false);
      return;
    }
    let cancelled = false;
    setLoadingSecret(true);
    setError(null);
    billingService
      .createSetupIntent()
      .then((res) => {
        if (!cancelled) setClientSecret(res.client_secret);
      })
      .catch(() => {
        if (!cancelled) setError("Could not start card setup.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSecret(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, billingConfigured, publishableKey]);

  const stripePromise = useMemo(
    () => (publishableKey ? stripePromiseFor(publishableKey) : null),
    [publishableKey]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add payment method</DialogTitle>
          <DialogDescription>
            Your card is saved securely with Stripe for off-session billing.
          </DialogDescription>
        </DialogHeader>
        {loadingSecret && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Preparing secure form…
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <SetupForm
              onDone={() => {
                onOpenChange(false);
              }}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SetupForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const attach = useAttachPaymentMethodMutation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const result = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    if (result.error) {
      setError(result.error.message || "Card setup failed.");
      setSubmitting(false);
      return;
    }
    const pmId =
      typeof result.setupIntent?.payment_method === "string"
        ? result.setupIntent.payment_method
        : result.setupIntent?.payment_method?.id;
    if (!pmId) {
      setError("Setup succeeded but no payment method was returned.");
      setSubmitting(false);
      return;
    }
    try {
      await attach.mutateAsync(pmId);
      onDone();
    } catch {
      setError("Card was verified but could not be saved. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button type="submit" disabled={!stripe || submitting || attach.isPending}>
          {(submitting || attach.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save card
        </Button>
      </DialogFooter>
    </form>
  );
}

export default Billing;
